import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    getCenterMembers,
    getCenterMember,
    createCenterMember,
    bulkCreateCenterMembers,
    updateCenterMember,
    deleteCenterMember,
    convertMemberType,
    getCenterMemberArchives,
} from './api';
import { publicMemberKeys } from './keys';
import { memberStatsKeys } from '../memberStats/keys';
import { invalidateScheduleListQueries } from '../../lib/scheduleQueryKeys';
import {
    CreateCenterMemberBody,
    UpdateCenterMemberBody,
    ConvertMemberTypeBody,
    MemberType,
    CenterMemberStatus,
    AppAccountStatus,
    FacilityStatus,
} from './types';

export const invalidateLegacyMemberQueries = async ({
    queryClient,
    centerId,
    memberId,
}: {
    queryClient: ReturnType<typeof useQueryClient>;
    centerId: string;
    memberId?: string;
}) => {
    await Promise.all([
        queryClient.invalidateQueries({ queryKey: publicMemberKeys.list(centerId) }),
        queryClient.invalidateQueries({ queryKey: ['getCenterMemberList'] }),
        queryClient.invalidateQueries({ queryKey: ['getCenterMemberListQuery'] }),
        queryClient.invalidateQueries({ queryKey: ['getCenterMemberListAPIQuery'] }),
        queryClient.invalidateQueries({ queryKey: ['getMemberListForCenterAPI'] }),
        queryClient.invalidateQueries({ queryKey: ['getTrainerMemberCountAPI'] }),
        queryClient.invalidateQueries({ queryKey: memberStatsKeys.stats(centerId) }),
        queryClient.invalidateQueries({ queryKey: ['memberCoach'] }),
        queryClient.invalidateQueries({ queryKey: ['newOpportunityCalendar'] }),
        queryClient.invalidateQueries({ queryKey: ['newOpportunities'] }),
        queryClient.invalidateQueries({ queryKey: ['newOpportunitySummary'] }),
        queryClient.invalidateQueries({ queryKey: ['memberProfileNewOpportunities'] }),
        ...(memberId
            ? [
                  queryClient.invalidateQueries({
                      queryKey: publicMemberKeys.detail(centerId, memberId),
                  }),
              ]
            : []),
    ]);
};

export const usePublicMemberList = ({
    centerId,
    memberType,
    status,
    search,
    hasPT,
    memberScope,
    coach,
    remainingPeriod,
    remainingSessions,
    displayStatus,
    facilityStatus,
    appAccountStatus,
    appLinked,
    page,
    limit,
    timeout,
    enabled = true,
}: {
    centerId: string;
    memberType?: MemberType | 'all';
    status?: CenterMemberStatus | 'all';
    search?: string;
    hasPT?: 'true' | 'false';
    memberScope?: 'all' | 'lesson' | 'public' | 'pt' | 'group';
    coach?: string;
    remainingPeriod?: string;
    remainingSessions?: string;
    displayStatus?: 'active' | 'pending' | 'expired' | 'all';
    facilityStatus?: FacilityStatus | 'all';
    appAccountStatus?: AppAccountStatus | 'all';
    /** @deprecated 신규 구현은 appAccountStatus 사용 */
    appLinked?: 'linked' | 'unlinked' | 'all';
    page?: number;
    limit?: number;
    timeout?: number;
    enabled?: boolean;
}) =>
    useQuery({
        queryKey: [
            ...publicMemberKeys.list(centerId),
            memberType,
            status,
            search,
            hasPT,
            memberScope,
            coach,
            remainingPeriod,
            remainingSessions,
            displayStatus,
            facilityStatus,
            appAccountStatus,
            appLinked,
            page,
            limit,
            timeout,
        ],
        queryFn: () =>
            getCenterMembers({
                centerId,
                memberType,
                status,
                search,
                hasPT,
                memberScope,
                coach,
                remainingPeriod,
                remainingSessions,
                displayStatus,
                facilityStatus,
                appAccountStatus,
                appLinked,
                page,
                limit,
                timeout,
            }),
        enabled: enabled && !!centerId,
        select: (data) => data?.data ?? undefined,
        keepPreviousData: true,
        staleTime: 30_000,
    });

// 센터 전체 회원을 한 번에 불러와 캐시하는 훅. 탭/검색/필터는 호출측에서 useMemo로 처리.
export const usePublicMemberListAll = ({
    centerId,
    enabled = true,
}: {
    centerId: string;
    enabled?: boolean;
}) =>
    useQuery({
        queryKey: [...publicMemberKeys.list(centerId), 'all'],
        queryFn: () => getCenterMembers({ centerId, limit: 5000, timeout: 60_000 }),
        enabled: enabled && !!centerId,
        select: (data) => data?.data ?? undefined,
        staleTime: 60_000,
    });

export const usePublicMemberDetail = ({
    centerId,
    memberId,
    enabled = true,
}: {
    centerId: string;
    memberId: string;
    enabled?: boolean;
}) =>
    useQuery({
        queryKey: publicMemberKeys.detail(centerId, memberId),
        queryFn: () => getCenterMember({ centerId, memberId }),
        enabled: enabled && !!centerId && !!memberId,
        select: (data) => {
            if (!data?.data) return data?.data;
            const raw = data.data as any;
            // API 응답: { centerMember, member, subscriptions, ... }
            // member 객체의 필드를 centerMember에 병합
            const merged = {
                ...raw,
                centerMember: {
                    ...raw.centerMember,
                    name: raw.centerMember?.name || raw.member?.name || '',
                    phoneNumber: raw.centerMember?.phoneNumber || raw.member?.phoneNumber || '',
                    memberType: raw.centerMember?.memberType || raw.member?.memberType || 'public',
                    gender: raw.centerMember?.gender || raw.member?.gender || '',
                    birthday: raw.centerMember?.birthday || raw.member?.birthday || '',
                    memo: raw.centerMember?.memo ?? raw.member?.memo ?? raw.memo ?? '',
                    appAccountStatus:
                        raw.centerMember?.appAccountStatus ?? raw.member?.appAccountStatus,
                    appWithdrawnAt:
                        raw.centerMember?.appWithdrawnAt ?? raw.member?.appWithdrawnAt,
                    appLinked: raw.centerMember?.appLinked ?? raw.member?.appLinked,
                    isInfo: raw.centerMember?.isInfo ?? raw.member?.isInfo,
                },
                memberTypeHistory: raw.memberTypeHistory ?? raw.member?.memberTypeHistory ?? [],
            };
            return merged;
        },
    });

export const useCreatePublicMember = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ centerId, body }: { centerId: string; body: CreateCenterMemberBody }) =>
            createCenterMember({ centerId, body }),
        onSuccess: (_, variables) => {
            void invalidateLegacyMemberQueries({
                queryClient,
                centerId: variables.centerId,
            });
        },
    });
};

export const useBulkCreateMembers = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ centerId, file }: { centerId: string; file: File }) =>
            bulkCreateCenterMembers({ centerId, file }),
        onSuccess: (_, variables) => {
            void invalidateLegacyMemberQueries({
                queryClient,
                centerId: variables.centerId,
            });
        },
    });
};

export const useUpdatePublicMember = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            centerId,
            memberId,
            body,
        }: {
            centerId: string;
            memberId: string;
            body: UpdateCenterMemberBody;
        }) => updateCenterMember({ centerId, memberId, body }),
        onSuccess: (_, variables) => {
            void invalidateLegacyMemberQueries({
                queryClient,
                centerId: variables.centerId,
                memberId: variables.memberId,
            });
            /**
             * 회원 표시명은 일정 문서에 **스냅샷으로 박혀 있다.** 그래서 이름을 고치면
             * 회원 도메인뿐 아니라 일정 목록 응답도 달라진다. 회원 키만 버리면
             * 일간·주간·목록·스마트 뷰는 전역 staleTime(1분, lib/queryClient.ts:15) 동안
             * 옛 이름을 그대로 보여준다.
             *
             * 이름이 바뀌었는지 따지지 않고 항상 버린다. 이 훅은 메모만 고치는 경로와도
             * 공유되지만(PublicMemberDetailPage `handleMemoUpdate`), 이 화면에는 일정 목록
             * 쿼리를 구독하는 곳이 없어서 **요청이 그 자리에서 나가지 않는다** — stale 표시가
             * 전부고, 비용은 다음에 일정 화면을 열 때 조회 한 번이 앞당겨지는 정도다.
             * 반대로 이름 비교를 넣으면 직전 이름을 캐시에서 읽어야 하는데, 그 캐시는 바로
             * 위 무효화가 동시에 다시 채우는 중이라 경합에 걸린다. 싸지도 않은 방어다.
             */
            invalidateScheduleListQueries(queryClient);
        },
    });
};

export const useDeletePublicMember = () => {
    const queryClient = useQueryClient();
    return useMutation({
        /**
         * 공유 풀 정리 인자를 **그대로 통과시킨다.**
         *
         * 예전에는 `{ centerId, memberId }` 만 뽑아 넘겨서, 호출부가 confirmShareCleanup 을
         * 실어 보내도 여기서 조용히 사라졌다. 관리자는 취소 목록까지 확인했는데 계속
         * 409 를 보게 된다 — 눌러도 아무것도 안 되는 자리다.
         */
        mutationFn: (params: {
            centerId: string;
            memberId: string;
            confirmShareCleanup?: boolean;
            expectedItemIds?: string[];
        }) => deleteCenterMember(params),
        onSuccess: (_, variables) => {
            void invalidateLegacyMemberQueries({
                queryClient,
                centerId: variables.centerId,
                memberId: variables.memberId,
            });
        },
    });
};

export const useMemberArchiveList = ({
    centerId,
    search,
    page,
    limit,
    enabled = true,
}: {
    centerId: string;
    search?: string;
    page?: number;
    limit?: number;
    enabled?: boolean;
}) =>
    useQuery({
        queryKey: [...publicMemberKeys.all, 'archives', centerId, search, page, limit],
        queryFn: () => getCenterMemberArchives({ centerId, search, page, limit }),
        enabled: enabled && !!centerId,
        select: (data) => data?.data ?? undefined,
    });

export const useConvertMemberType = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            centerId,
            memberId,
            body,
        }: {
            centerId: string;
            memberId: string;
            body: ConvertMemberTypeBody;
        }) => convertMemberType({ centerId, memberId, body }),
        onSuccess: (_, variables) => {
            void invalidateLegacyMemberQueries({
                queryClient,
                centerId: variables.centerId,
                memberId: variables.memberId,
            });
        },
    });
};
